<template>
  <transition name="modal" id="product-list-dialog" v-if="showPopup">
    <DxPopup
      class="vertical-center"
      ref="productSelector"
      id="dx-dialog"
      width="100vw"
      height="100vh"
      :title="$t('common.filters')"
      :hide-on-outside-click="false"
      :show-close-button="true"
      :visible="showPopup"
      :resize-enabled="true"
      :drag-enabled="false"
      @hidden="buttonCloseEvent"
    >
      <template #title>
        <div class="filter_title_template">
          <div class="left_side">
            <img src="@/assets/images/icons/arrow-back.svg" @click="goBackOneStep" />
            <span @click="goBackOneStep">{{ $t("common.back") }}</span>
          </div>
          <div class="right_side">
            <img class="close_button" src="@/assets/images/icons/dialog-close.svg" @click="buttonCloseEvent" />
          </div>
        </div>
      </template>
      <template #content>
        <div class="filters_tabs">
          <DxTabPanel
            :data-source="filterSteps"
            :loop="false"
            :selected-index="currentStep"
            :animation-enabled="true"
            :swipe-enabled="false"
            :show-nav-buttons="false"
            @selection-changed="tabSelectionChanged($event)"
          >
            <template #item="{ data: step }">
              <div>
                <div v-if="step == 1" class="filter_container" :use-native="true">
                  <ProductList
                    ref="productGridWithAdd"
                    @selectionChanged="productSelected"
                    :action-method="selectFromStock ? loadStockProductsPage : getProducts"
                    :dataColumns="productComboColumns"
                    :localDataset="stockProductList"
                    :hasError="false"
                    :allowAddNew="allowAddNew"
                    :placeholder="$t('stock_mobile.form.select_product')"
                    :filterData="filterData"
                    :transactionType="transactionType"
                  >
                  </ProductList>
                </div>
                <div v-if="step == 2" class="filter_container" :use-native="true">
                  <div class="filter_header_container">
                    <div class="filter_header">{{ transactionType }} \ {{ $t("stock_mobile.form.added_products") }}</div>
                    <div class="new_item_add_button" @click="goToStep(0)" v-if="isProductEditable">
                      <i class="fas fa-plus"></i>
                    </div>
                  </div>
                  <div v-if="hasHeaderMeta" class="filter_header_meta">
                    <div v-if="sourceWarehouseName || destWarehouseName" class="filter_header_meta_item">
                      {{ sourceWarehouseName ?? "-" }} --> {{ destWarehouseName ?? "-" }}
                    </div>
                    <div v-if="ordersLabel" class="filter_header_meta_item">{{ ordersLabel }}</div>
                  </div>
                  <ProductMultiManager
                    ref="productMultiManager"
                    :products="localSelectedProducts"
                    :movement-date="movementDate"
                    :total-area="totalArea"
                    :show-warehouse-selector="showWarehouseSelector"
                    :show-unit-price="showUnitPrice"
                    :bottom-padding="productListBottomPadding"
                    :is-product-editable="isProductEditable"
                    @input="updateProducts"
                  />
                  <div class="bottom_sticky product-selector-sticky" v-show="currentStep == 1 && productsValid">
                    <div class="sticky_action_button" @click="continueSelection">{{ $t("common.continue") }}</div>
                  </div>
                </div>
              </div>
            </template>
          </DxTabPanel>
          <toast-message ref="toastMessageProductSelector"></toast-message>
        </div>
      </template>
    </DxPopup>
  </transition>
</template>

<script>
import { mapActions } from "vuex";
import { DxPopup } from "devextreme-vue/popup";
import DxTabPanel from "devextreme-vue/tab-panel";
import ToastMessage from "@/components/Mobile/toastMessage.vue";
import ProductList from "./ProductList.vue";
import ProductMultiManager from "@/components/Mobile/ProductMultiManager.vue";

export default {
  name: "ProductSelectorMultiManager",
  props: {
    showPopup: {
      type: Boolean,
      default: true,
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    selectFromStock: {
      type: Boolean,
      default: false,
    },
    allowAddNew: {
      type: Boolean,
      default: true,
    },
    filterData: {
      type: Object,
      default: null,
    },
    movementDate: {
      type: String,
      default: "",
    },
    transactionType: {
      type: String,
      default: "",
    },
    totalArea: {
      type: Number,
      default: 0,
    },
    showWarehouseSelector: {
      type: Boolean,
      default: false,
    },
    showUnitPrice: {
      type: Boolean,
      default: false,
    },
    sourceWarehouseName: {
      type: String,
      default: "",
    },
    destWarehouseName: {
      type: String,
      default: "",
    },
    ordersLabel: {
      type: String,
      default: "",
    },
    isProductEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      filterSteps: [1, 2],
      currentStep: 0,
      previousStep: null,
      productsValid: false,
      productComboColumns: this.selectFromStock
        ? [
            { dataField: "product_name_code", caption: this.$t("stock_mobile.form.product_code") },
            { dataField: "warehouse_name", caption: this.$t("common.warehouse") },
            { dataField: "warehouse_quantity_m", caption: this.$t("common.quantity"), width: "25%" },
          ]
        : [
            { dataField: "code", caption: this.$t("stock_mobile.form.code"), width: "25%" },
            { dataField: "name", caption: this.$t("common.name") },
          ],
      stockProductList: [],
      localSelectedProducts: [],
      stockPager: {
        cache: [],
        nextPage: 1,
        hasMorePages: true,
        lastQueryKey: null,
      },
    };
  },
  computed: {
    hasHeaderMeta() {
      return !!(this.sourceWarehouseName || this.destWarehouseName || this.ordersLabel);
    },
    productListBottomPadding() {
      return this.currentStep == 1 && this.productsValid ? 90 : 0;
    },
  },
  components: {
    DxPopup,
    DxTabPanel,
    ToastMessage,
    ProductList,
    ProductMultiManager,
  },
  watch: {
    showPopup(newVal) {
      if (newVal) {
        this.localSelectedProducts = this.normalizeProducts(this.selectedProducts);
        if (this.selectedProducts && this.selectedProducts.length > 0 && !!this.movementDate) {
          this.currentStep = 1;
        } else {
          this.currentStep = 0;
        }
        this.setTitle();
        this.refreshProductsValid();
      }
    },
    "filterData.department_legal_entity_uuid": {
      handler: function () {
        this.resetStockPagerAndRefresh();
      },
      deep: true,
    },
    "filterData.warehouse_uuid": {
      handler: function () {
        this.resetStockPagerAndRefresh();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      getProducts: "smProducts/getSmProducts",
      getSmProductsFull: "smProducts/getSmProductsFull",
    }),
    tabSelectionChanged(e) {
      this.previousStep = e.removedItems ? this.filterSteps.indexOf(e.removedItems[0]) ?? -1 : -1;
      this.currentItem = e.addedItems[0];
      this.currentStep = this.filterSteps.indexOf(this.currentItem);
    },
    setTitle() {
      if (this.currentStep == 0) {
        this.$emit("setTitle", this.$t("stock_mobile.form.product_reception"), true);
      } else if (this.currentStep == 1) {
        this.$emit("setTitle", this.$t("stock_mobile.form.products_list"), false);
      }
    },
    goBackOneStep() {
      if (this.previousStep === 1 && this.currentStep === 0) {
        this.currentStep = 1;
        this.setTitle();
        return;
      }
      this.buttonCloseEvent();
    },
    goToStep(step) {
      this.currentStep = step;
      this.setTitle();
    },
    buttonCloseEvent() {
      this.$emit("hidden");
    },
    productSelected(selectedProduct) {
      if (selectedProduct && selectedProduct.uuid) {
        const exists = this.localSelectedProducts.find(
          (product) =>
            product.uuid === selectedProduct.uuid ||
            (product.uuid.toString().startsWith("NEW_") &&
              selectedProduct.uuid.toString().startsWith("NEW_") &&
              product.name === selectedProduct.name),
        );

        if (exists) {
          this.$refs.toastMessageProductSelector.showError(this.$t("stock_mobile.form.error_product_already_added"));
          return;
        }

        selectedProduct.measure_unit_id = selectedProduct?.measure_unit?.id ?? null;
        this.localSelectedProducts.push({ ...selectedProduct, lot_items: [] });
        this.currentStep = 1;
        this.setTitle();
      }
    },
    updateProducts(products) {
      this.localSelectedProducts = this.normalizeProducts(products);
      this.$emit("input", this.localSelectedProducts);
      this.refreshProductsValid();
    },
    normalizeProducts(products) {
      return (Array.isArray(products) ? products : []).map((product) => ({
        ...product,
        lot_items: Array.isArray(product?.lot_items) ? product.lot_items : [],
      }));
    },
    refreshProductsValid() {
      this.$nextTick(() => {
        this.productsValid = this.$refs.productMultiManager?.isValid?.() ?? false;
      });
    },
    continueSelection() {
      const isValid = this.$refs.productMultiManager?.validate?.() ?? false;
      if (!isValid) {
        this.productsValid = false;
        return;
      }
      this.$emit("input", this.localSelectedProducts);
      this.buttonCloseEvent();
    },
    resetStockPager() {
      this.stockPager.cache = [];
      this.stockPager.nextPage = 1;
      this.stockPager.hasMorePages = true;
      this.stockPager.lastQueryKey = null;
    },
    resetStockPagerAndRefresh() {
      if (!this.selectFromStock) {
        return;
      }

      this.resetStockPager();
      this.$nextTick(() => {
        this.$refs.productGridWithAdd?.refreshData?.();
      });
    },
    getStockQueryKey(loadOptions) {
      return JSON.stringify({
        department_legal_entity_uuid: this.filterData?.department_legal_entity_uuid ?? null,
        warehouse_uuid: this.filterData?.warehouse_uuid ?? null,
        filter: loadOptions?.filter ?? null,
        sort: loadOptions?.sort ?? null,
      });
    },
    mapStockRows(stocks) {
      const rows = [];

      (stocks || []).forEach((stock) => {
        if (this.selectFromStock) {
          (stock.stock_list || []).forEach((stock_data) => {
            rows.push({
              ...stock,
              product_name_code: stock.code ? stock.name + " (" + stock.code + ")" : stock.name,
              warehouse_uuid: stock_data.warehouse_uuid,
              warehouse_name: stock_data.warehouse_name,
              warehouse_quantity: parseFloat(stock_data.quantity),
              warehouse_quantity_m: parseFloat(stock_data.quantity) + " " + stock.measure_unit.symbol,
              warehouse_department_legal_entity_uuid: stock_data.legal_entity_uuid,
              warehouse: {
                uuid: stock_data.warehouse_uuid,
                name: stock_data.warehouse_name,
                quantity: parseFloat(stock_data.quantity),
                quantity_m: parseFloat(stock_data.quantity) + " " + stock.measure_unit.symbol,
                warehouse_department_legal_entity_uuid: stock_data.legal_entity_uuid,
              },
              lot_management: stock.lot_management,
              lot_items: stock_data.lot_items || [],
            });
          });
        } else {
          rows.push({
            ...stock,
            product_name_code: stock.code ? stock.name + " (" + stock.code + ")" : stock.name,
            warehouse_uuid: null,
            warehouse_name: null,
            warehouse_quantity: 0,
            warehouse_department_legal_entity_uuid: null,
            lot_management: stock.lot_management,
            lot_items: [],
          });
        }
      });

      return rows;
    },
    async loadStockProductsPage(loadOptions) {
      const take = Number(loadOptions?.take) || 30;
      const skip = Number(loadOptions?.skip) || 0;
      const pageLimit = take;

      const currentQueryKey = this.getStockQueryKey(loadOptions);
      if (this.stockPager.lastQueryKey !== currentQueryKey) {
        this.resetStockPager();
        this.stockPager.lastQueryKey = currentQueryKey;
      }

      while (this.stockPager.hasMorePages && this.stockPager.cache.length < skip + take) {
        const payload = {
          page: this.stockPager.nextPage,
          limit: pageLimit,
          has_stock: 1,
          department_legal_entity_uuid: this.filterData?.department_legal_entity_uuid ?? null,
          warehouse_uuid: this.filterData?.warehouse_uuid ?? null,
          filter: loadOptions?.filter,
          sort: loadOptions?.sort,
        };

        const response = await this.getSmProductsFull(payload);
        const products = response?.data?.data || [];
        const pagination = response?.data?.pagination || {};

        const mappedRows = this.mapStockRows(products);
        this.stockPager.cache.push(...mappedRows);

        const totalPages = Number(pagination.total_pages || 0);
        const noMoreByCount = products.length < pageLimit;
        const noMoreByPage = totalPages > 0 && this.stockPager.nextPage >= totalPages;

        this.stockPager.hasMorePages = !(noMoreByCount || noMoreByPage);
        this.stockPager.nextPage += 1;

        if (products.length === 0) {
          break;
        }
      }

      const data = this.stockPager.cache.slice(skip, skip + take);

      return {
        data,
        totalCount: this.stockPager.hasMorePages
          ? Math.max(this.stockPager.cache.length + 1, skip + data.length + 1)
          : this.stockPager.cache.length,
      };
    },
  },
  mounted() {
    this.resetStockPagerAndRefresh();
  },
};
</script>

<style scoped>
.filter_header_meta {
  font-size: 14px;
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
}

.filter_header_meta_item {
  line-height: 1.4;
}

.filter_header_container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter_header {
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
}

.new_item_add_button {
  flex: 0 0 auto;
  min-width: 32px;
}

.product-selector-sticky {
  position: fixed;
  top: auto;
  bottom: 0;
  left: 0;
  z-index: 2005;
  width: 100vw;
  max-width: 100vw;
}
</style>
